import { APP_DOMAIN, PROTOCOL, HANDLE_SUBDOMAIN, ENVIRONMENT, PRODUCTION, STAGING, VALIDATION } from 'constants';
import * as Sentry from '@sentry/react';
Sentry.init({
  dsn: 'https://89cb829344f1c729b0f372128a90404b@o4508997986615296.ingest.de.sentry.io/4509014295445584',
  environment: ENVIRONMENT,
  enabled: STAGING || VALIDATION || PRODUCTION,
});
import { initHyperDX } from 'services/hyperdx';
initHyperDX();

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Helmet } from 'react-helmet';
import { ReactNotifications } from 'react-notifications-component';
import ApplicationContainer from 'apps/main/container';
import { getCurrentSubDomain } from 'services/sub_domain';
import I18nContext from 'contexts/i18n/context';
import I18nProvider from 'contexts/i18n/provider';
import WithStore from 'store/with_store';
import WithInvitationIntegrationToken from 'components/with_integration_invitation_token_parameter';
import favicon from 'images/favicon.png';
import { BrowserRouter } from 'react-router-dom';
import ExternalServicesProvider from 'contexts/external_services/provider';
import OnRouteChange from 'services/on_route_change';
import MaintenanceMode from 'services/maintenance_mode';
import CurrentBrandProvider from 'contexts/current_brand/provider';
import ConfigProvider from 'contexts/config/provider';
import SessionProvider from 'contexts/session/provider';
import SSOProvider from 'contexts/sso/provider';
import RedirectForAnonymous from 'services/redirect_for_anonymous';
import ChunkErrorHandler from 'services/chunk_error_handler';
import BackSocketProvider from 'contexts/back_socket/provider';
import WithParamsToken from 'components/with_params_token';

import './application.scss';

const InsurgateFront = () => {
  const subDomain = getCurrentSubDomain();

  if (subDomain === null && HANDLE_SUBDOMAIN) {
    window.location = `${PROTOCOL}://${APP_DOMAIN}`;
    return null;
  }

  return (
    <ChunkErrorHandler>
      <MaintenanceMode>
        <RedirectForAnonymous />
        <WithParamsToken>
          <ExternalServicesProvider>
            <I18nProvider>
              <ConfigProvider>
                <CurrentBrandProvider>
                  <WithStore>
                    <BackSocketProvider>
                      <SessionProvider>
                        <SSOProvider>
                          <I18nContext.Consumer>
                            {({ locale }) => {
                              return (
                                <Helmet>
                                  <meta charSet="utf-8" />
                                  <meta name="viewport" content="width=device-width, initial-scale=1" />

                                  {/* Favicon (one line is enough; add sizes if you have multiple) */}
                                  <link rel="icon" href={favicon} type="image/png" />

                                  {/* Resource hints for fonts */}
                                  <link rel="preconnect" href="https://fonts.googleapis.com" />
                                  <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                                  <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
                                  <link rel="dns-prefetch" href="https://fonts.gstatic.com" />

                                  {/* Preload Google Fonts stylesheet to avoid blocking render */}
                                  <link rel="preload" as="style" href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" />
                                  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" media="print" />
                                  <title>Insurgate</title>
                                </Helmet>
                              );
                            }}
                          </I18nContext.Consumer>
                          <ReactNotifications />
                          <WithInvitationIntegrationToken>
                            <BrowserRouter>
                              <ApplicationContainer />
                              <OnRouteChange />
                            </BrowserRouter>
                          </WithInvitationIntegrationToken>
                        </SSOProvider>
                      </SessionProvider>
                    </BackSocketProvider>
                  </WithStore>
                </CurrentBrandProvider>
              </ConfigProvider>
            </I18nProvider>
          </ExternalServicesProvider>
        </WithParamsToken>
      </MaintenanceMode>
    </ChunkErrorHandler>
  );
};
const container = document.getElementById('root');
const root = createRoot(container);
root.render(<InsurgateFront />);
